/**
 * Utility functions for flight restriction zones
 */

/**
 * Converts a zone name to a URL-safe slug
 */
export function slugifyZoneName(name: string | undefined | null): string {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Extracts zone name from slug
 */
export function deslugifyZoneName(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Finds a zone by name slug from a list of zones
 * Handles both GeoJSON features and transformed zone objects
 */
export function findZoneBySlug(zones: any[], slug: string) {
  if (!zones || !Array.isArray(zones) || !slug) return null;
  
  return zones.find(zone => {
    if (!zone) return false;
    
    // Handle GeoJSON feature structure (properties.name)
    const zoneName = zone.properties?.name || zone.name;
    if (!zoneName) return false;
    
    return slugifyZoneName(zoneName) === slug;
  });
}

/**
 * Extracts useful information from zone.data object
 */
export function extractZoneData(zoneData: Record<string, any>) {
  const extracted = {
    name: zoneData.Name || '',
    restrictions: [] as Array<{
      description: string;
      upperLimit: string;
      lowerLimit: string;
      class: string | null;
    }>,
    otherData: {} as Record<string, any>
  };

  // Extract restriction information
  Object.entries(zoneData).forEach(([key, value]) => {
    if (key === 'Name') {
      extracted.name = value;
    } else if (key.includes('Upper limit:') && key.includes('Lower limit:')) {
      // This is a restriction description
      const parts = key.split('<br/>');
      const description = parts[0] || '';
      const upperLimit = parts.find(p => p.includes('Upper limit:'))?.replace('Upper limit:', '').trim() || '';
      const lowerLimit = parts.find(p => p.includes('Lower limit:'))?.replace('Lower limit:', '').trim() || '';
      const classInfo = parts.find(p => p.includes('Class:'))?.replace('Class:', '').trim() || null;

      extracted.restrictions.push({
        description,
        upperLimit,
        lowerLimit,
        class: classInfo
      });
    } else {
      // Store other data
      extracted.otherData[key] = value;
    }
  });

  return extracted;
}

/**
 * Formats restriction information for display
 */
export function formatRestriction(restriction: {
  description: string;
  upperLimit: string;
  lowerLimit: string;
  class: string | null;
}) {
  return {
    ...restriction,
    formattedDescription: restriction.description.replace(/<br\/>/g, '\n'),
    hasClass: restriction.class && restriction.class !== 'null'
  };
}
